<template>
  <div class="dashboard-view">
    <opportunity-dashboard-filters
      v-if="showFilters"
      :pipelines="pipelines"
      :agents="agents"
      :inboxes="inboxes"
      :disabled="isFetching"
      :initial-filters="filters"
      @filter-applied="onFilterApplied"
    />

    <woot-loading-state
      v-if="isFetching"
      :message="$t('OPPORTUNITY_DASHBOARD.LOADING')"
    />
    <empty-state
      v-else-if="!filters.pipelineId"
      :title="$t('OPPORTUNITY_DASHBOARD.SELECT_PIPELINE_TITLE')"
      :message="$t('OPPORTUNITY_DASHBOARD.SELECT_PIPELINE_MESSAGE')"
    />

    <div v-else-if="dashboardData" class="dashboard-view__content">
      <div class="summary-grid">
        <opportunity-dashboard-summary-card
          icon="deal-icon"
          color="blue"
          :title="$t('OPPORTUNITY_DASHBOARD.SUMMARY.CREATED')"
          :value="`${dashboardData.summary.count}`"
          :subtitle="formatCurrency(dashboardData.summary.value)"
        />
        <opportunity-dashboard-summary-card
          icon="arrow-trending-lines"
          color="green"
          :title="$t('OPPORTUNITY_DASHBOARD.SUMMARY.CONVERSION_RATE')"
          :value="`${Math.round(dashboardData.conversion.rate)}%`"
          :subtitle="
            $t('OPPORTUNITY_DASHBOARD.SUMMARY.CONVERSION_DETAIL', {
              won: dashboardData.conversion.won_count,
              value: formatCurrency(dashboardData.conversion.won_value),
            })
          "
        />
        <opportunity-dashboard-summary-card
          icon="receipt"
          color="yellow"
          :title="$t('OPPORTUNITY_DASHBOARD.SUMMARY.AVERAGE_TICKET')"
          :value="formatCurrency(dashboardData.average_ticket)"
          :subtitle="averageCycleLabel"
        />
        <opportunity-dashboard-summary-card
          icon="warning"
          color="red"
          :title="$t('OPPORTUNITY_DASHBOARD.SUMMARY.LOST')"
          :value="`${dashboardData.lost.count}`"
          :subtitle="formatCurrency(dashboardData.lost.value)"
        />
      </div>

      <opportunity-dashboard-open-card
        :open="dashboardData.open"
        :sla-breakdown="dashboardData.open.sla_breakdown"
        :currency="selectedPipelineCurrency"
      />

      <div class="two-column-grid">
        <opportunity-dashboard-stage-funnel
          :stages="dashboardData.stages"
          :currency="selectedPipelineCurrency"
        />
        <opportunity-dashboard-loss-reasons
          :loss-reasons="dashboardData.loss_reasons"
          :currency="selectedPipelineCurrency"
        />
      </div>

      <opportunity-dashboard-owners-table
        :owners="dashboardData.owners"
        :currency="selectedPipelineCurrency"
      />
    </div>
  </div>
</template>

<script>
import { debounce } from '@chatwoot/utils';
import alertMixin from 'shared/mixins/alertMixin';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import EmptyState from 'dashboard/components/widgets/EmptyState';
import {
  DEFAULT_DASHBOARD_FILTERS,
  buildOpportunityDashboardParams,
  formatCurrencyValue,
} from 'dashboard/helper/opportunityDashboardHelper';
import OpportunityDashboardFilters from './OpportunityDashboardFilters.vue';
import OpportunityDashboardSummaryCard from './OpportunityDashboardSummaryCard.vue';
import OpportunityDashboardOpenCard from './OpportunityDashboardOpenCard.vue';
import OpportunityDashboardStageFunnel from './OpportunityDashboardStageFunnel.vue';
import OpportunityDashboardLossReasons from './OpportunityDashboardLossReasons.vue';
import OpportunityDashboardOwnersTable from './OpportunityDashboardOwnersTable.vue';

export default {
  components: {
    EmptyState,
    OpportunityDashboardFilters,
    OpportunityDashboardSummaryCard,
    OpportunityDashboardOpenCard,
    OpportunityDashboardStageFunnel,
    OpportunityDashboardLossReasons,
    OpportunityDashboardOwnersTable,
  },
  mixins: [alertMixin],
  props: {
    pipelines: { type: Array, default: () => [] },
    agents: { type: Array, default: () => [] },
    inboxes: { type: Array, default: () => [] },
    initialFilters: { type: Object, default: () => ({}) },
    showFilters: { type: Boolean, default: true },
  },
  data() {
    return {
      filters: {
        ...DEFAULT_DASHBOARD_FILTERS(),
        ...this.initialFilters,
      },
      dashboardData: null,
      isFetching: false,
    };
  },
  computed: {
    selectedPipelineCurrency() {
      const pipeline = this.pipelines.find(
        item => item.id === this.filters.pipelineId
      );
      return pipeline?.default_currency || 'BRL';
    },
    averageCycleLabel() {
      const days = this.dashboardData?.average_cycle_days;
      if (days === null || days === undefined) {
        return this.$t('OPPORTUNITY_DASHBOARD.SUMMARY.AVERAGE_CYCLE_EMPTY');
      }
      return this.$t('OPPORTUNITY_DASHBOARD.SUMMARY.AVERAGE_CYCLE', {
        days: Math.round(days),
      });
    },
  },
  created() {
    this.debouncedRefreshDashboard = debounce(this.loadDashboard, 800);
  },
  mounted() {
    if (!this.filters.pipelineId) {
      const [firstActivePipeline] = this.pipelines.filter(
        pipeline => pipeline.status === 'active'
      );
      if (firstActivePipeline) {
        this.filters = { ...this.filters, pipelineId: firstActivePipeline.id };
      }
    }

    this.$emit('filters-applied', this.filters);

    if (this.filters.pipelineId) {
      this.loadDashboard();
    }

    window.bus.$on('opportunity_updated', this.handleOpportunityUpdatedEvent);
  },
  beforeDestroy() {
    window.bus.$off('opportunity_updated', this.handleOpportunityUpdatedEvent);
  },
  methods: {
    handleOpportunityUpdatedEvent(data) {
      if (!this.filters.pipelineId) return;
      if (Number(data?.pipeline_id) !== Number(this.filters.pipelineId)) return;

      this.debouncedRefreshDashboard();
    },
    formatCurrency(value) {
      return formatCurrencyValue(value, this.selectedPipelineCurrency);
    },
    onFilterApplied(filters) {
      this.filters = filters;
      this.$emit('pipeline-selected', filters.pipelineId);
      this.$emit('filters-applied', filters);
      this.loadDashboard();
    },
    async loadDashboard() {
      if (!this.filters.pipelineId) {
        this.dashboardData = null;
        return;
      }

      this.isFetching = true;
      this.$emit('total-changed', null);
      try {
        this.dashboardData = await this.$store.dispatch(
          'opportunities/dashboard',
          buildOpportunityDashboardParams(this.filters)
        );
        this.$emit('total-changed', this.dashboardData.summary.count);
      } catch (error) {
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DASHBOARD.LOAD_ERROR')
        );
      } finally {
        this.isFetching = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dashboard-view__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-normal);
}

.summary-grid {
  display: grid;
  gap: var(--space-normal);
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
}

.two-column-grid {
  display: grid;
  gap: var(--space-normal);
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
}
</style>
